// 🔧 volunteering.js
// ✅ Purpose: Show assigned volunteer duties to logged-in users
// ✅ Features:
// - Only visible to users with role: 'volunteer'
// - Loads assigned activities and time slots for current event
// - Shows each time slot with date/time and activity title
// - Displays check-in instructions from event settings (if available)
// - Styled like other user-facing pages
// - Gracefully handles if no assignments exist

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  arrayRemove,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth_context";
import { formatDateTime } from "../../utils/date_format";
import { logger } from '../../utils/logger';
import { showStatus } from "../../utils/status_helpers";
import FullPageSpinner from "../../components/FullPageSpinner";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { sanitizeHtml } from "../../utils/sanitize_html";

const Volunteering = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [checkinInstructions, setCheckinInstructions] = useState("");
  const [status, setStatus] = useState(null);
  const [contactNote, setContactNote] = useState("");
  const [volunteerGoodies, setVolunteerGoodies] = useState("");
  const [volunteerThanks, setVolunteerThanks] = useState("");
  
  useEffect(() => {
    if (!eventId || !user) return;

    const fetchAssignments = async () => {
      try {
        // ✅ Load all volunteer activities for this event
        const allActivitiesSnap = await getDocs(collection(db, "events", eventId, "volunteers"));
        logger.log("📦 Volunteer activities loaded:", { value: allActivitiesSnap.docs.length });

        const allAssignments = [];

        for (const docSnap of allActivitiesSnap.docs) {
          const activity = { id: docSnap.id, ...docSnap.data() };

          try {
            // ✅ Load all slots under each volunteer activity
            const slotSnap = await getDocs(
              collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots")
            );
            logger.log(`📦 Loaded Volunteer slots for activity ${activity.title}`, { value: slotSnap.docs.length });

            for (const slotDoc of slotSnap.docs) {
              const slot = slotDoc.data();
              const assignedUsers = slot.assignedUsers || [];

              const isAssignedToCurrentUser = assignedUsers.some(
                (assignedUser) => assignedUser.uid === user.uid
              );

              if (isAssignedToCurrentUser) {
                  allAssignments.push({
                  ...slot,
                  slotId: slotDoc.id,
                  activityId: docSnap.id,
                  activityTitle: activity.title,
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                });
              }
            }

          } catch (slotError) {
            logger.error(`❌ Error loading Volunteer timeSlots for activity ${activity.id}`, {
              value: slotError
            });
          }
        }

        allAssignments.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        setAssignments(allAssignments);
        logger.log("✅ Final volunteer assignments:", { value: allAssignments.length });

      } catch (err) {
        logger.error("❌ Error loading volunteer activities:", { value: err });
      }
    };

    const fetchCheckinInstructions = async () => {
      try {
        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const settings = settingsSnap.exists()
          ? settingsSnap.data()
          : {};

        setCheckinInstructions(
          settings?.volunteerCheckin || "..."
        );

        setContactNote(
          settings?.contactNote || ""
        );

        setVolunteerGoodies(
          settings?.volunteerGoodies || ""
        );

        setVolunteerThanks(
          settings?.volunteerThanks || ""
        );
      } catch (err) {
        logger.error(
          "❌ Failed loading volunteer settings",
          {
            value: err,
          }
        );
      }
    };

    fetchAssignments();
    fetchCheckinInstructions();
  }, [eventId, user]);

  const handleCancelAssignment = async (slotId, activityId) => {
    try {
      const slotRef = doc(db, "events", eventId, "volunteers", activityId, "timeSlots", slotId);
      const slotSnap = await getDoc(slotRef);
      const slotData = slotSnap.exists() ? slotSnap.data() : {};

      const updatedAssignedUsers = (slotData.assignedUsers || []).filter(
        (assignedUser) => assignedUser.uid !== user.uid
      );

      await updateDoc(slotRef, {
        assignedUsers: updatedAssignedUsers,

        // ✅ Keep legacy field synced during migration
        assignedUserIds: arrayRemove(user.uid),
      });

      // Remove locally
      setAssignments(prev => prev.filter(s => !(s.slotId === slotId && s.activityId === activityId)));
  
      // Show success
      showStatus(setStatus, {
        type: "success",
        message: "You have been removed from this volunteer duty.",
      });

    } catch (err) {
      logger.error("❌ Error canceling volunteer slot:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to cancel. Please try again.",
      });

    }
  };

  // =====================================================
  // 🔎 Dynamic SEO
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Volunteering",
      path: `/${eventslug}/volunteering`,
    },
  ];

  const approvedVolunteeringSeo = buildEventPageSeo({
    eventDoc,
    pageKey: "volunteering",

    fallbackTitle: `${eventDoc?.name || "Event"} Volunteering | Let's Go Vote`,

    fallbackDescription:
      `View volunteer opportunities, duties, and check-in information for ${
        eventDoc?.name || "this event"
      }.`,
  });

  const volunteeringSeo = {
    ...approvedVolunteeringSeo,

    path: `/${eventslug}/volunteering`,

    breadcrumbs,

    noindex: true,
  };

  if (!ready || eventLoading || !user || !eventId) {
    return <FullPageSpinner message="Loading volunteer duties..." />;
  }

  return (
  <>
    <SeoHead {...volunteeringSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🙋 My Volunteer Duties</h1>
          <p className="lead text-muted">See your assigned activities and check-in info</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            } fade-in`}
          >
            {status.message}
          </div>
        )}

        <>
          {assignments.length === 0 && (
            <>
              <p className="text-muted">
                You do not currently have any volunteer duties assigned for this event.
              </p>
              <div className="alert alert-info">
                <p>
                  Volunteering is a great way to support the event and meet new people! If you’re interested
                  in helping out, stop by the <strong>Event Promoter’s booth</strong>, or{" "}
                  <Link to={`/${eventslug}/volunteer_opportunities`} className="text-decoration-underline">
                    browse available volunteer opportunities
                  </Link>{" "}
                  to sign up for a shift.
                </p>
              </div>
            </>
          )}

          {assignments.length > 0 && (
            <>
            <h5 className="mb-3 text-primary">📍 Check-in Instructions</h5>
              <p className="mb-4">{checkinInstructions}</p>

              {assignments.map((slot, index) => (
                <div
                  key={index}
                  className="mb-4 border-start border-3 ps-3 bg-light"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        🗓️ {formatDateTime(slot.startTime)} – {formatDateTime(slot.endTime)}
                      </h6>
                      <p className="mb-0">💼 <strong>{slot.activityTitle}</strong></p>
                    </div>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleCancelAssignment(slot.slotId, slot.activityId)}
                    >
                      Cancel Volunteering
                    </button>
                  </div>
                </div>
              ))}
              </>
            )}

            <hr className="my-4" />

            {/* 💬 Contact Info (Editable) */}
            {contactNote && (
              <div className="mb-3">
                <h5 className="text-primary">📞 Contact the Event Promoter</h5>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(contactNote) }} />
              </div>
            )}

            {/* 🎁 Volunteer Perks */}
            {volunteerGoodies && (
              <div className="mb-3">
                <h5 className="text-primary">🎁 Volunteer Goodies</h5>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(volunteerGoodies) }} />
              </div>
            )}

            {/* 🙏 Thanks (Editable) */}
            {volunteerThanks && (
              <div className="alert alert-success mt-4">
                <h5>🙏 Thank You!</h5>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(volunteerThanks) }} />
              </div>
            )}
          </>

      </div>
    </section>
    </>
  );
};

export default Volunteering;
